// 🔧 audit.js
// ✅ Purpose: Admin-only page to review all votes cast in an event
// ✅ Features:
// - Loads votes from event-scoped collection
// - Loads cars from event-scoped collection
// - Loads users from global collection
// - Shows voter name, item info, category, and vote time
// - Filters by category and name search
// - Exports to XLSX
// - Uses event-level categories via useCategories()

import React, { useEffect, useState, useMemo } from "react";
import { db } from "../../firebase";
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import * as XLSX from "xlsx"; 
import { useCategories } from "../../hooks/use_categories";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import { useEventContext } from "../../hooks/use_event_context";
import {
  getEventVotingType,
  isStandardVotingEvent,
} from "../../utils/event_voting_type_helpers";
import {
  getVotingTypeLabel,
} from "../../utils/voting_engine_registry";
import {
  isRankedEliminationStrategy,
  supportsJudgedResults,
} from "../../utils/result_strategy_helpers";

const AdminAudit = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  if (eventLoading || !ready) {
    return <FullPageSpinner message="Loading Audit..." />;
  }

  return (
    <AdminAuditContent
      eventId={eventId}
      eventslug={eventslug}
      eventDoc={eventDoc}
    />
  );
};

const AdminAuditContent = ({
  eventId,
  eventslug,
  eventDoc,
}) => {
  const { categories } = useCategories(eventId);
  const [votes, setVotes] = useState([]);
  const [users, setUsers] = useState({});
  const [cars, setCars] = useState({});
  const [filterCat, setFilterCat] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const votesPerPage = 50;
  const [status, setStatus] = useState(null);
  const votingType = getEventVotingType(eventDoc);
  const isStandardVoting = isStandardVotingEvent(eventDoc);
  const votingTypeLabel = getVotingTypeLabel(votingType);
  const isRankedAudit = isRankedEliminationStrategy(votingType);
  const isJudgedAudit = supportsJudgedResults(votingType);

  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCat, search]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // 🗳 STANDARD VOTING ONLY
        // This page assumes each vote document represents
        // one vote for one item in one category.
        const voteSnap = await getDocs(collection(db, "events", eventId, "votes"));
        const votesData = voteSnap.docs.map((d) => ({
          ...d.data(),
          id: d.id,
          cid: d.data().cid || d.data().id || d.id,
        }));

        // =====================================================
        // 👥 Load only users that actually voted
        // =====================================================

        const voterIds = [
          ...new Set(
            votesData
              .map((vote) => vote.uid)
              .filter(Boolean)
          ),
        ];

        const userMap = {};

        for (let i = 0; i < voterIds.length; i += 10) {

          const chunk = voterIds.slice(i, i + 10);

          const usersQuery = query(
            collection(db, "users"),
            where(documentId(), "in", chunk)
          );

          const userSnap = await getDocs(usersQuery);

          userSnap.forEach((docSnap) => {

            const userData = {
              id: docSnap.id,
              ...docSnap.data(),
            };

            userMap[docSnap.id] = userData;

          });
        }

        const carSnap = await getDocs(collection(db, "events", eventId, "cars"));

        const carMap = {};

        carSnap.docs.forEach((docSnap) => {
          const car = {
            id: docSnap.id,
            ...docSnap.data(),
          };

          // ✅ Primary key: Firestore car document id
          carMap[docSnap.id] = car;

          // ✅ Fallback keys for legacy references
          if (car.id) carMap[car.id] = car;
          if (car.cid) carMap[car.cid] = car;
          if (car.regcnt) carMap[String(car.regcnt)] = car;
        });

        setVotes(votesData);
        setUsers(userMap);
        setCars(carMap);
      } catch (err) {
        logger.error("❌ Failed to load audit data:", {
          value: err
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [eventId]);

  const categoriesMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      map[String(cat.id)] = cat.name;
    });
    return map;
  }, [categories]);

  const filteredVotes = votes.filter((v) => {
    if (Number(v.catid) === 0) return false;

    // ✅ When viewing Ranked Choice audit, only show ranked-choice vote docs
    if (votingType === "ranked_choice" && v.votingType !== "ranked_choice") {
      return false;
    }

    // ✅ When viewing Standard audit, only show standard/legacy vote docs
    if (votingType === "standard" && v.votingType === "ranked_choice") {
      return false;
    }

    // ✅ When viewing Judged Points audit,
    // only show Judged Points vote docs
    if (votingType === "points" && v.votingType !== "points") {
      return false;
    }
    
    const user = users[v.uid] || {};
    const matchSearch = `${user.firstName} ${user.lastName}`.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || String(v.catid) === filterCat;
    return matchSearch && matchCat;
  });

  const totalPages = Math.ceil(filteredVotes.length / votesPerPage);
  const paginatedVotes = filteredVotes.slice(
    (currentPage - 1) * votesPerPage,
    currentPage * votesPerPage
  );

  const exportToXLSX = () => {
    const exportData = filteredVotes
    .map((v) => {
      if (Number(v.catid) === 0) return null;
      const user = users[v.uid] || {};
      const car = cars[v.cid] || {};
      const category = categoriesMap?.[v.catid] || "Unknown";

      return {
        Voter: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        Category: category,
        Item: `${car.description || ""}`.trim(),
        RegCnt: car.regcnt || "",
        Owner: `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim(),
        VotedAt: v.createdAt?.toDate?.().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) || "",
      };
    })
    .filter(Boolean); // 🧹 Remove nulls

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit");
    XLSX.writeFile(wb, `Vote_Audit_${eventslug}.xlsx`);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalAuditVotes = filteredVotes.length;

  const uniqueVoters = new Set(
    filteredVotes.map((v) => v.uid).filter(Boolean)
  ).size;

  const categoriesRepresented = new Set(
    filteredVotes.map((v) => String(v.catid)).filter(Boolean)
  ).size;

  const currentPageVotes = paginatedVotes.length;

  if (loading) {
    return <div className="container mt-5"><FullPageSpinner message="Loading Audit Data..." /></div>;
  }

  if (
    !isStandardVoting &&
    !isRankedAudit &&
    !isJudgedAudit
  ) {
      return (
      <section className="container-fluid py-5">
        <div className="alert alert-warning">
          Audit reporting for <strong>{votingType}</strong>
          voting has not been implemented yet.
        </div>
      </section>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">📋 Vote Audit</h1>
            <p className="lead text-muted mb-0">
              Review submitted votes, filter audit records, and export event voting data.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button className="btn btn-outline-success" onClick={exportToXLSX}>
              📤 Export Audit
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Audit Records</div>
                <div className="fs-3 fw-bold">{totalAuditVotes}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Unique Voters</div>
                <div className="fs-3 fw-bold">{uniqueVoters}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Categories</div>
                <div className="fs-3 fw-bold">{categoriesRepresented}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Page Results</div>
                <div className="fs-3 fw-bold">{currentPageVotes}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Vote Audit Tools</strong>
          </div>

          <div className="card-body">
            <div className="alert alert-info mb-4">
              <strong>Voting Type:</strong> {votingTypeLabel}
            </div>

            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label">Filter by Category</label>
                <select
                  className="form-select"
                  value={filterCat}
                  onChange={(e) => setFilterCat(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories
                    .filter((cat) => Number(cat.id) !== 0)
                    .sort((a, b) => {
                      const sortA = Number.isFinite(Number(a?.sortOrder))
                        ? Number(a.sortOrder)
                        : Number.isFinite(Number(a?.id))
                        ? Number(a.id)
                        : 9999;

                      const sortB = Number.isFinite(Number(b?.sortOrder))
                        ? Number(b.sortOrder)
                        : Number.isFinite(Number(b?.id))
                        ? Number(b.id)
                        : 9999;

                      if (sortA !== sortB) {
                        return sortA - sortB;
                      }

                      return String(a?.name ?? "").localeCompare(
                        String(b?.name ?? ""),
                        undefined,
                        { sensitivity: "base" }
                      );
                    })
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label">Search by Voter</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="First or last name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-4">
                <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearch("");
                      setFilterCat("");
                    }}
                  >
                    🔄 Reset
                  </button>

                  <button className="btn btn-outline-success" onClick={exportToXLSX}>
                    📤 Export
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
            <div>
              <strong>Audit Records</strong>
              <div className="small text-muted">
                Showing {paginatedVotes.length} of {filteredVotes.length} records
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped align-middle">
              <thead className="table-light">
                <tr>
                  <th>Voter</th>
                  <th>Category</th>

                  {isRankedAudit ? (
                    <th>Rankings</th>
                  ) : isJudgedAudit ? (
                    <>
                      <th>Entry</th>
                      <th>Total Score</th>
                      <th>Score Breakdown</th>
                    </>
                  ) : (
                    <th>Entry</th>
                  )}
                </tr>
              </thead>
                <tbody>
                  {paginatedVotes.map((v) => {
                    const user = users[v.uid] || {};
                    const car = cars?.[v.cid] || {};
                    const catName = categoriesMap?.[v.catid] || "Unknown";

                    const description = car?.description || "—";
                    const regcnt = car?.regcnt || "—";
                    const ownerName =
                      `${car?.ownerFirst || ""} ${car?.ownerLast || ""}`.trim() ||
                      "—";
                    const voteTime =
                      v.createdAt?.toDate?.().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      }) || "—";

                    return (
                      <tr key={v.id}>
                        <td>
                          <div className="fw-bold">
                            {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                              "Unknown Voter"}
                          </div>

                          <div className="small text-muted">
                            {voteTime}
                          </div>
                        </td>

                        <td>{catName}</td>

                        {isRankedAudit ? (
                          <td>
                            {Array.isArray(v.rankings) && v.rankings.length > 0 ? (
                              <ol className="mb-0">
                                {v.rankings
                                  .sort((a, b) => Number(a.rank) - Number(b.rank))
                                  .map((ranking) => {
                                    const rankedCar =
                                      cars?.[ranking.cid] ||
                                      cars?.[String(ranking.cid)] ||
                                      {};

                                    return (
                                      <li key={`${ranking.rank}-${ranking.cid}`}>
                                        #{rankedCar.regcnt || "?"} —{" "}
                                        {rankedCar.description || "Unknown Entry"}
                                      </li>
                                    );
                                  })}
                              </ol>
                            ) : (
                              <span className="text-muted">No rankings</span>
                            )}
                          </td>
                        ) : isJudgedAudit ? (
                          <>
                            <td>
                              <div className="fw-bold">{description}</div>

                              <div className="small text-muted">
                                Owner: {ownerName}
                              </div>

                              <div className="small text-muted">
                                Reg #: {regcnt}
                              </div>
                            </td>

                            <td>
                              <strong>
                                {v.totalPoints || 0} / {v.maxBallotPoints || 100}
                              </strong>
                            </td>

                            <td>
                              {(v.scores || []).length > 0 ? (
                                <div className="d-flex flex-wrap gap-2">
                                  {(v.scores || []).map((score) => (
                                    <div
                                      key={score.factorId}
                                      className="border rounded p-2 bg-light"
                                      style={{ minWidth: "180px" }}
                                    >
                                      <div className="fw-bold small">
                                        {score.factorName}
                                      </div>

                                      <div className="small">
                                        {score.pointsAwarded} / {score.maxPoints}
                                      </div>

                                      {(score.checkedHighlights || []).some(
                                        (highlight) => Number(highlight.maxPoints || 0) > 0
                                      ) && (
                                        <div className="mt-2">
                                          {(score.checkedHighlights || []).map((highlight) => {
                                            if (Number(highlight.maxPoints || 0) <= 0) {
                                              return null;
                                            }

                                            return (
                                              <div
                                                key={highlight.highlightId}
                                                className="small text-muted"
                                              >
                                                • {highlight.highlightName}:{" "}
                                                {highlight.pointsAwarded || 0} /{" "}
                                                {highlight.maxPoints}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted">No score breakdown</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <td>
                            <div className="fw-bold">{description}</div>

                            <div className="small text-muted">
                              Owner: {ownerName}
                            </div>

                            <div className="small text-muted">
                              Reg #: {regcnt}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}

                  {!paginatedVotes.length && (
                    <tr>
                      <td
                        colSpan={isRankedAudit ? 3 : isJudgedAudit ? 5 : 3}
                        className="text-center py-5"
                      >
                        <h5>No Votes Found</h5>
                        <p className="text-muted mb-0">
                          No audit records match the current filters.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4 d-print-none">
                <button
                  className="btn btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                    scrollToTop();
                  }}
                >
                  ⬅ Prev
                </button>

                <div className="d-flex align-items-center gap-2">
                  <span>Page</span>

                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={currentPage}
                    onChange={(e) => {
                      setCurrentPage(Number(e.target.value));
                      scrollToTop();
                    }}
                  >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      )
                    )}
                  </select>

                  <span>of {totalPages}</span>
                </div>

                <button
                  className="btn btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                    scrollToTop();
                  }}
                >
                  Next ➡
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );

};

export default AdminAudit;
